import { useEffect, useRef, useState } from "react";

// Glassy tinted backgrounds for colorful mode — indexed by face value minus 1 (0=1pip … 5=6pips)
const COLORFUL_GRADIENTS = [
  "linear-gradient(150deg, rgba(255,230,130,0.85) 0%, rgba(230,180,50,0.65) 50%, rgba(200,140,20,0.50) 100%)",  // 1 — gold
  "linear-gradient(150deg, rgba(175,215,255,0.85) 0%, rgba(80,155,240,0.65) 50%, rgba(30,100,220,0.50) 100%)",  // 2 — blue
  "linear-gradient(150deg, rgba(175,255,195,0.85) 0%, rgba(80,210,110,0.65) 50%, rgba(30,170,65,0.50) 100%)",   // 3 — green
  "linear-gradient(150deg, rgba(255,205,140,0.85) 0%, rgba(240,150,50,0.65) 50%, rgba(210,100,20,0.50) 100%)",  // 4 — orange
  "linear-gradient(150deg, rgba(220,180,255,0.85) 0%, rgba(160,100,245,0.65) 50%, rgba(110,45,210,0.50) 100%)", // 5 — violet
  "linear-gradient(150deg, rgba(255,185,185,0.85) 0%, rgba(230,70,70,0.65) 50%, rgba(195,30,30,0.50) 100%)",   // 6 — red
] as const;

const COLORFUL_BORDERS = [
  "rgba(210,165,40,0.80)",   // 1 gold
  "rgba(50,130,220,0.80)",   // 2 blue
  "rgba(50,185,80,0.80)",    // 3 green
  "rgba(220,120,30,0.80)",   // 4 orange
  "rgba(135,60,220,0.80)",   // 5 violet
  "rgba(210,45,45,0.80)",    // 6 red
] as const;

interface Props {
  value: number;
  size?: "sm" | "md" | "lg" | "xl";
  isRolling?: boolean;
  isHeld?: boolean;
  colorful?: boolean;
  colorIndex?: number; // 0‑4
  colorValue?: number; // kept for API compat; unused
  className?: string;
  color?: string;
  dotColor?: string;
}

// Pip positions as [left%, top%] for each face value.
// Matches standard Western die layout.
const PIPS: Record<number, Array<[number, number]>> = {
  1: [[50, 50]],
  2: [
    [72, 28],
    [28, 72],
  ],
  3: [
    [72, 28],
    [50, 50],
    [28, 72],
  ],
  4: [
    [28, 28],
    [72, 28],
    [28, 72],
    [72, 72],
  ],
  5: [
    [28, 28],
    [72, 28],
    [50, 50],
    [28, 72],
    [72, 72],
  ],
  6: [
    [28, 25],
    [72, 25],
    [28, 50],
    [72, 50],
    [28, 75],
    [72, 75],
  ],
};

const SIZES = {
  sm: { box: 32, pip: 6 },
  md: { box: 48, pip: 9 },
  lg: { box: 64, pip: 12 },
  xl: { box: 88, pip: 16 },
};

export default function Dice({
  value,
  size = "md",
  isRolling = false,
  isHeld = false,
  colorful = false,
  className = "",
}: Props) {
  const { box, pip } = SIZES[size];
  const pips = PIPS[value] ?? PIPS[1];
  // Color is determined by face value (1=gold, 2=blue, 3=green, 4=orange, 5=violet, 6=red)
  const idx = Math.max(0, Math.min(5, value - 1));

  // When isRolling transitions false→true, start "land" animation
  const prevRolling = useRef(isRolling);
  const [landing, setLanding] = useState(false);

  useEffect(() => {
    if (prevRolling.current && !isRolling) {
      setLanding(true);
      const t = setTimeout(() => setLanding(false), 450);
      prevRolling.current = false;
      return () => clearTimeout(t);
    }
    prevRolling.current = isRolling;
  }, [isRolling]);

  const animClass = isRolling ? "dice-rolling" : landing ? "dice-landing" : "";

  return (
    <div
      className={`dice-scene ${className}`}
      style={{ width: box, height: box, flexShrink: 0 }}
    >
      <div
        className={`dice-body ${isHeld ? "dice-held" : ""} ${animClass}`}
        style={{
          width: box,
          height: box,
          position: "relative",
          ...(colorful && !isHeld
            ? {
                background: COLORFUL_GRADIENTS[idx],
                borderColor: COLORFUL_BORDERS[idx],
              }
            : {}),
        }}
      >
        {pips.map(([left, top], i) => (
          <div
            key={i}
            className="dice-pip"
            style={{
              width: pip,
              height: pip,
              left: `${left}%`,
              top: `${top}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
